import { showToast } from '@/components/Toasts';
import type { UploadedImage } from '@/types';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const MIN_DIMENSION = 640;
const TARGET_SIZE = 1024;

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
const MAGIC_BYTES: Record<string, number[]> = {
  'image/jpeg': [0xff, 0xd8, 0xff],
  'image/png': [0x89, 0x50, 0x4e, 0x47],
  'image/webp': [0x52, 0x49, 0x46, 0x46],
};

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export async function validateFile(file: File): Promise<ValidationResult> {
  // Size check
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Image must be smaller than 20MB.' };
  }

  // MIME type check
  if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      error: 'Please upload a JPG, PNG, or HEIC image.',
    };
  }

  // Magic bytes check (first 4 bytes)
  const header = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  const expectedMagic = MAGIC_BYTES[file.type.toLowerCase()];
  if (expectedMagic && !expectedMagic.every((b, i) => header[i] === b)) {
    return { valid: false, error: 'File type does not match its contents.' };
  }

  // Dimension check
  const dimensions = await getImageDimensions(file);
  if (dimensions.width < MIN_DIMENSION || dimensions.height < MIN_DIMENSION) {
    return {
      valid: false,
      error: `Image must be at least ${MIN_DIMENSION}×${MIN_DIMENSION} pixels.`,
    };
  }

  return { valid: true };
}

function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });
}

export function getOrientation(file: File): Promise<number> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const view = new DataView(e.target?.result as ArrayBuffer);
      if (view.getUint16(0, false) !== 0xffd8) {
        resolve(1);
        return;
      }
      const length = view.byteLength;
      let offset = 2;
      while (offset < length) {
        if (view.getUint16(offset, false) !== 0xffe1) {
          if (view.getUint16(offset, false) === 0xffda) break;
          offset += 2 + view.getUint16(offset + 2, false);
          continue;
        }
        if (view.getUint32(offset + 8, false) !== 0x45786966) {
          offset += 2 + view.getUint16(offset + 2, false);
          continue;
        }
        const little = view.getUint16(offset + 14, false) === 0x4949;
        const firstOffset = view.getUint32(offset + 18, little);
        const tags = view.getUint16(offset + 18 + firstOffset, little);
        for (let i = 0; i < tags; i++) {
          const tagOffset = offset + 18 + firstOffset + 2 + i * 12;
          if (view.getUint16(tagOffset, little) === 0x0112) {
            resolve(view.getUint16(tagOffset + 8, little));
            return;
          }
        }
        break;
      }
      resolve(1);
    };
    reader.onerror = () => resolve(1);
    reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
  });
}

export async function preprocessImage(file: File): Promise<{
  bitmap: ImageBitmap;
  url: string;
  width: number;
  height: number;
  orientation: number;
}> {
  const orientation = await getOrientation(file);

  let img: ImageBitmap;
  try {
    img = await createImageBitmap(file);
  } catch {
    throw new Error('Failed to decode image. The file may be corrupt or unsupported.');
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // Calculate resize preserving aspect ratio
  let { width, height } = img;
  const maxDim = Math.max(width, height);
  if (maxDim > TARGET_SIZE) {
    const scale = TARGET_SIZE / maxDim;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  // Handle orientation
  if (orientation > 4) {
    canvas.width = height;
    canvas.height = width;
  } else {
    canvas.width = width;
    canvas.height = height;
  }

  ctx.save();
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  ctx.translate(cx, cy);

  switch (orientation) {
    case 2:
      ctx.scale(-1, 1);
      break;
    case 3:
      ctx.rotate(Math.PI);
      break;
    case 4:
      ctx.scale(1, -1);
      break;
    case 5:
      ctx.rotate(Math.PI / 2);
      ctx.scale(1, -1);
      break;
    case 6:
      ctx.rotate(Math.PI / 2);
      break;
    case 7:
      ctx.rotate(-Math.PI / 2);
      ctx.scale(1, -1);
      break;
    case 8:
      ctx.rotate(-Math.PI / 2);
      break;
  }

  ctx.drawImage(img, -width / 2, -height / 2, width, height);
  ctx.restore();

  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => {
      if (!b) {
        reject(new Error('Canvas export failed. The image may be too large.'));
        return;
      }
      resolve(b);
    }, 'image/jpeg', 0.92)
  );

  const bitmap = await createImageBitmap(blob);
  const url = URL.createObjectURL(blob);

  img.close?.();

  return { bitmap, url, width: canvas.width, height: canvas.height, orientation };
}

export async function handleFileSelect(
  file: File,
  onProgress?: (message: string) => void
): Promise<UploadedImage | null> {
  onProgress?.('Validating image...');

  try {
    const validation = await validateFile(file);
    if (!validation.valid) {
      showToast(validation.error!, 'error');
      return null;
    }

    onProgress?.('Preparing image...');

    const { bitmap, url, width, height, orientation } = await preprocessImage(file);
    bitmap.close?.();

    return {
      id: crypto.randomUUID(),
      file,
      sourceUrl: url,
      width,
      height,
      orientation,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to process image';
    showToast(message, 'error');
    return null;
  }
}
